import api from "@/services/api";
import {
  reactive,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
} from "vue";
import { useQuery } from "@tanstack/vue-query";
import { isHigherVersion, parseVersion } from "@/utils/versions";

export type Version = string;

function getVersion(): Promise<Version> {
  // The endpoint returns {version, csrf_token} (since the ensure_csrf_cookie
  // addition) — only the version string is the query's data.
  return api
    .get<{ version: string }>("/version/")
    .then((response) => response.data.version);
}

export function useVersionQuery(pollWhile: MaybeRefOrGetter<boolean> = true) {
  return reactive(
    useQuery({
      queryKey: ["version"],
      queryFn: getVersion,
      // Self-disabling INTERVAL rather than `enabled`: /version/ doubles as
      // the ensure_csrf_cookie carrier, so the query must stay enabled (focus
      // refetches keep the cookie fresh) even after polling stops. Hidden
      // tabs don't poll either way — refetchOnWindowFocus catches them up on
      // refocus, which is the first moment a banner can be seen.
      refetchInterval: () => (toValue(pollWhile) ? 30 * 1000 : false),
    }),
  );
}

export function useNewVersionCheck(): Ref<boolean> {
  const newVersionAvailable = ref(false);
  // Once latched there is nothing left to learn — stop polling entirely.
  const version = useVersionQuery(() => !newVersionAvailable.value);
  // The bundle knows its own version at build time; the server reporting a
  // higher one means this tab is running stale code. If the build-time version
  // is ever missing or unparsable — the silent failure mode that killed this
  // banner once before — fall back to the first parsable polled version as
  // the baseline, so the check can never silently disarm again.
  let baseline = parseVersion(String(import.meta.env.VITE_APP_VERSION ?? ""));
  watch(
    () => version.data,
    (serverVersion) => {
      if (!serverVersion || newVersionAvailable.value) {
        return;
      }
      const server = parseVersion(serverVersion);
      if (server === null) {
        return;
      }
      if (baseline === null) {
        baseline = server;
        return;
      }
      if (isHigherVersion(server, baseline)) {
        newVersionAvailable.value = true;
      }
    },
  );
  return newVersionAvailable;
}
